import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuSeparator } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ArrowLeft, Plus, Trash2, Save, Building, User, Package, Calculator, ChevronDown, Check, Edit, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { invoiceApiService, InvoiceStatus, CompleteInvoiceCreateRequest, CompleteInvoiceUpdateRequest, ChargeMaster } from '../services/invoiceApi';
import { branchApiService, Branch } from '../services/branchApi';
import { customerApiService, Customer } from '../services/customerApi';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import { endgoodCostMasterApiService } from '../services/endgoodCostMasterApi';
import { salesOrderApiService, SalesOrder, SalesOrderDetail } from '../services/salesOrderApi';
import { packingApiService, BoxType, EndgoodBoxType, PackingDetail } from '../services/packingApi';
import { hsnApiService, HsnCode } from '../services/hsnApi';
import { transporterMasterApiService, TransporterMaster, getTransporterDisplayName } from '../services/transporterMasterApi';
import { useBranch } from '../contexts/BranchContext';
import { getInvoiceConfig, isSupplyTypeWithoutTax } from '../config/uiConfig';

interface InvoiceFormData {
  invoiceNo: string;
  invoiceDate: string;
  branchId: string;
  shipToId: string;
  billToId: string;
  salesOrderId: string;
  deliverNote: string;
  referenceNoAndDate: string;
  otherReferences: string;
  additionalRemarks: string;
  buyerOrderNo: string;
  buyerOrderDate: string;
  dispatchDocNo: string;
  deliverNoteDate: string;
  dispatchedThrough: string;
  ewayTransportMode: string;
  destination: string;
  billOfLanding: string;
  motorVehicleNo: string;
  /** Selected transporter master DB id (optional) */
  transporterMasterId: string;
  ewayDistanceKm: string;
  termOfDelivery: string;
  /** e-invoice supply type (supTyp) — e.g. B2B, SEZWP, SEZWOP */
  regularSupplyCode: string;
  status: InvoiceStatus;
  invoiceDetails: InvoiceDetailFormData[];
  otherCharges: OtherChargeFormData[];
}

interface InvoiceDetailFormData {
  id: number;
  endgoodId: string;
  uom: string;
  qty: number;
  rate: number;
  hsn: string;
  cgst: number;
  sgst: number;
  igst: number;
  workOrderId: string;
  typeOfGood: string;
  boxes: string;
  selectedPackingDetailIds: number[]; // Array of selected PackingDetail IDs
  heatNo: string;
  userProvidedHeatNo: string;
  amount: number;
  manualQuantityRate?: boolean; // when true, quantity and rate are editable
  allowRateEdit?: boolean; // when true, only rate is editable for non-manual goods
  isManualEntry?: boolean; // when true, description and HSN are manual; no endgood
  manualDescription?: string; // free-text description when isManualEntry or isServiceEntry
  isServiceEntry?: boolean; // when true, service line: description, HSN, amount; qty/rate optional
}

interface OtherChargeFormData {
  id: number;
  chargeMasterId: string;
  description: string;
  amount: number;
  rate: number;
  sequenceOrder: number;
  applicableOn?: string;
}

/** TCS other charges: round amount to nearest whole rupee. */
function roundTcsToWholeRupee(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return Math.round(value);
}

function textLooksLikeTcs(s: string | undefined | null): boolean {
  if (!s || typeof s !== 'string') return false;
  const u = s.toUpperCase();
  return u.includes('TCS') || u.includes('T.C.S') || u.includes('TAX COLLECTED AT SOURCE');
}

function isTcsOtherCharge(charge: OtherChargeFormData, masters: ChargeMaster[]): boolean {
  if (textLooksLikeTcs(charge.description)) return true;
  if (!charge.chargeMasterId) return false;
  const cm = masters.find((c) => String(c.id) === String(charge.chargeMasterId));
  if (!cm) return false;
  return (
    textLooksLikeTcs(cm.chargeName) ||
    textLooksLikeTcs(cm.description) ||
    textLooksLikeTcs(cm.chargeCode)
  );
}

function isValidDistanceInput(value: string): boolean {
  return value === '' || /^\d+(\.\d?)?$/.test(value);
}

function normalizeDateOnly(value?: string | null): string {
  if (!value) return '';
  return value.split(/[T ]/)[0];
}

function customerSelectSearchText(customer: Customer): string {
  return [
    customer.customerName,
    customer.firmName,
    customer.stateName,
    customer.stateCode,
    customer.gstNo,
    customer.vendorCode,
    customer.customerCode,
  ]
    .filter((s) => s != null && String(s).trim() !== '')
    .map((s) => String(s).trim())
    .join(' ');
}

function CustomerSelectOptionLabel({ customer }: { customer: Customer }) {
  const stateLine = [customer.stateName, customer.stateCode].filter(Boolean).join(' — ');
  return (
    <div className="flex flex-col gap-0.5 py-0.5 text-left">
      <span className="font-medium leading-snug">{customer.customerName}</span>
      <span className="text-xs text-gray-500 leading-snug">
        {stateLine || 'State: —'} · GSTIN: {customer.gstNo?.trim() || '—'}
      </span>
    </div>
  );
}

export interface InvoiceFormProps {
  /** When provided, form loads this invoice and updates on save (edit). When omitted, form creates a new invoice. */
  invoiceId?: string;
}

interface HsnWiseRow {
  hsn: string;
  taxableValue: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  totalTaxAmount: number;
}

function amountInWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const convertHundreds = (n: number): string => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) result += ones[n] + ' ';
    return result.trim();
  };
  if (num === 0) return 'Zero';
  const whole = Math.floor(num);
  const paise = Math.round((num % 1) * 100);
  const numStr = whole.toString();
  let result = '';
  if (numStr.length > 7) {
    const crores = parseInt(numStr.slice(0, numStr.length - 7), 10);
    if (crores > 0) result += convertHundreds(crores) + ' Crore ';
  }
  if (numStr.length > 5) {
    const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5), 10);
    if (lakhs > 0) result += convertHundreds(lakhs) + ' Lakh ';
  }
  if (numStr.length > 3) {
    const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3), 10);
    if (thousands > 0) result += convertHundreds(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)), 10);
  if (hundreds > 0) result += convertHundreds(hundreds);
  result = result.trim() + ' Rupees';
  if (paise > 0) result += ' and ' + convertHundreds(paise) + ' Paise';
  return result.trim();
}

export const InvoiceForm: React.FC<InvoiceFormProps> = ({ invoiceId }) => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const isEdit = Boolean(invoiceId);
  const currentDate = useMemo(() => new Date().toISOString().split('T')[0], []);
  const invoiceConfig = getInvoiceConfig();
  const defaultTransportMode = invoiceConfig.modesOfTransportation.includes('ROAD') ? 'ROAD' : '';
  const regularSupplyOptions = Array.isArray((invoiceConfig as any).regularSupplies)
    ? ((invoiceConfig as any).regularSupplies as { code: string; name: string; description?: string }[])
    : [];
  const defaultRegularSupplyCode =
    regularSupplyOptions.find((o) => String(o.code).toUpperCase() === 'B2B')?.code ?? 'B2B';

  const [formData, setFormData] = useState<InvoiceFormData>({
    invoiceNo: '',
    invoiceDate: currentDate,
    branchId: selectedBranch?.toString() || '',
    shipToId: '',
    billToId: '',
    salesOrderId: '',
    deliverNote: '',
    referenceNoAndDate: '',
    otherReferences: '',
    additionalRemarks: '',
    buyerOrderNo: '',
    buyerOrderDate: '',
    dispatchDocNo: '',
    deliverNoteDate: '',
    dispatchedThrough: '',
    ewayTransportMode: defaultTransportMode,
    destination: '',
    billOfLanding: '',
    motorVehicleNo: '',
    transporterMasterId: '',
    ewayDistanceKm: '',
    termOfDelivery: '',
    regularSupplyCode: defaultRegularSupplyCode,
    status: InvoiceStatus.DRAFT,
    invoiceDetails: [],
    otherCharges: []
  });

  const [branches, setBranches] = useState<Branch[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [endgoods, setEndgoods] = useState<Endgood[]>([]);
  const [packedEndgoods, setPackedEndgoods] = useState<Endgood[]>([]);
  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [transporters, setTransporters] = useState<TransporterMaster[]>([]);
  const [chargeMasters, setChargeMasters] = useState<any[]>([]);
  const [boxTypes, setBoxTypes] = useState<BoxType[]>([]);
  const [endgoodBoxTypes, setEndgoodBoxTypes] = useState<EndgoodBoxType[]>([]);
  const [availableBoxesPerEndgood, setAvailableBoxesPerEndgood] = useState<{[endgoodId: string]: PackingDetail[]}>({});
  const [selectedSeller, setSelectedSeller] = useState<Branch | null>(null);
  const [selectedShipTo, setSelectedShipTo] = useState<Customer | null>(null);
  const [selectedBillTo, setSelectedBillTo] = useState<Customer | null>(null);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrder | null>(null);
  const [salesOrderDetails, setSalesOrderDetails] = useState<SalesOrderDetail[]>([]);
  const [billToSameAsShipTo, setBillToSameAsShipTo] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [editLoadState, setEditLoadState] = useState<'idle' | 'loading' | 'ok' | 'not_found'>(
    () => (invoiceId ? 'loading' : 'idle')
  );
  const [boxSelectionDetailIndex, setBoxSelectionDetailIndex] = useState<number | null>(null);
  const [isTransporterDialogOpen, setIsTransporterDialogOpen] = useState(false);
  const [newTransporter, setNewTransporter] = useState({
    transporterId: '',
    transporterName: '',
  });
  // Draft strings for Qty/Rate inputs so users can type decimals like `0.5`
  // (avoids parseFloat/onChange forcing the value to an integer while typing).
  const [qtyDrafts, setQtyDrafts] = useState<Record<number, string>>({});
  const [rateDrafts, setRateDrafts] = useState<Record<number, string>>({});
  const [boxAutoSelectCount, setBoxAutoSelectCount] = useState<number>(0);
  const [boxSelectedIds, setBoxSelectedIds] = useState<number[]>([]);
  const [hsnCodes, setHsnCodes] = useState<HsnCode[]>([]);
  const [originalCreateDate, setOriginalCreateDate] = useState<string>('');
  const isMounted = useRef(true);
  const effectiveChargeMasters = useMemo(() => {
    const invoiceDate = normalizeDateOnly(formData.invoiceDate);
    const masters = Array.isArray(chargeMasters) ? chargeMasters : [];
    if (!invoiceDate) return masters;
    const latestByChargeName = new Map<string, any>();

    masters.forEach((cm) => {
      const effectiveStartDate = normalizeDateOnly(cm?.effectiveStartDate);
      if (effectiveStartDate && effectiveStartDate > invoiceDate) return;

      const key = String(cm?.chargeName ?? cm?.id ?? '').trim().toUpperCase();
      const existing = latestByChargeName.get(key);
      const existingDate = normalizeDateOnly(existing?.effectiveStartDate);

      if (!existing || effectiveStartDate > existingDate) {
        latestByChargeName.set(key, cm);
      }
    });

    return Array.from(latestByChargeName.values()).sort((a, b) =>
      String(a?.chargeName ?? '').localeCompare(String(b?.chargeName ?? ''), undefined, {
        sensitivity: 'base',
      })
    );
  }, [chargeMasters, formData.invoiceDate]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [branchesRes, customersRes, endgoodsRes, salesOrdersRes, transportersRes, chargeMastersRes, boxTypesRes, endgoodBoxTypesRes, hsnCodesRes] = await Promise.all([
          branchApiService.getBranches(),
          customerApiService.getCustomers(),
          endgoodApiService.getEndgoodsIdName(),
          salesOrderApiService.getSalesOrdersByStatus('OPEN', selectedBranch ?? undefined),
          transporterMasterApiService.getAll().catch(() => ({ success: false as const, data: [] as TransporterMaster[], message: '' })),
          invoiceApiService.getChargeMasters(1, 500),
          packingApiService.getBoxTypes(),
          packingApiService.getEndgoodBoxTypes(),
          hsnApiService.getHsnCodes().catch(() => [])
        ]);

        if (isMounted.current) {
          setBranches(branchesRes || []);
          if (customersRes.success) setCustomers(customersRes.data || []);
          const endgoodsList = endgoodsRes?.success ? endgoodsRes.data || [] : [];
          setEndgoods(endgoodsList);
          setPackedEndgoods(endgoodsList); // Use id-name endgoods for dropdown; available boxes fetched when user selects an endgood
          if (salesOrdersRes.success) setSalesOrders(salesOrdersRes.data || []);
          if (transportersRes.success && Array.isArray(transportersRes.data)) {
            setTransporters(transportersRes.data);
          }
          const chargeMastersData = chargeMastersRes?.success
            ? ((chargeMastersRes.data as any)?.content ?? chargeMastersRes.data)
            : (chargeMastersRes as any)?.content;
          const chargeList = Array.isArray(chargeMastersData) ? chargeMastersData : [];
          if (chargeList.length > 0) setChargeMasters(chargeList);
          if (boxTypesRes.success) setBoxTypes(boxTypesRes.data || []);
          if (endgoodBoxTypesRes.success) setEndgoodBoxTypes(endgoodBoxTypesRes.data || []);
          setHsnCodes(Array.isArray(hsnCodesRes) ? hsnCodesRes : []);
        }
      } catch (error) {
        // console.error('Error loading initial data:', error);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    loadInitialData();
  }, [isMounted]);

  // Load existing invoice data when editing
  useEffect(() => {
    const loadInvoiceData = async () => {
      if (isEdit && invoiceId) {
        try {
          setEditLoadState('loading');
          const response = await invoiceApiService.getInvoiceHeaderById(invoiceId);
          if (!response.success || !response.data) {
            setEditLoadState('not_found');
            return;
          }
          const invoice = response.data;
          const headerBranchId = selectedBranch != null ? Number(selectedBranch) : null;
          const invoiceBranchId = invoice.branchId != null ? Number(invoice.branchId) : null;
          if (
            headerBranchId == null ||
            invoiceBranchId == null ||
            headerBranchId !== invoiceBranchId
          ) {
            setEditLoadState('not_found');
            return;
          }
          setOriginalCreateDate(normalizeDateOnly(invoice.createDate));
          setFormData({
              invoiceNo: invoice.invoiceNo || '',
              invoiceDate: normalizeDateOnly(invoice.invoiceDate) || currentDate,
              branchId: selectedBranch?.toString() || '',
              shipToId: invoice.shipTo?.toString() || invoice.billTo?.toString() || '',
              billToId: invoice.billTo?.toString() || '',
              salesOrderId: invoice.salesOrderId?.toString() || '',
              deliverNote: invoice.deliverNote || '',
              referenceNoAndDate: invoice.referenceNoAndDate || '',
              otherReferences: invoice.otherReferences || '',
              additionalRemarks: invoice.additional_remarks || invoice.additionalRemarks || '',
              buyerOrderNo: invoice.buyerOrderNo || '',
              buyerOrderDate: normalizeDateOnly(invoice.buyerOrderDate),
              dispatchDocNo: invoice.dispatchDocNo || '',
              deliverNoteDate: normalizeDateOnly(invoice.deliverNoteDate),
              dispatchedThrough: invoice.dispatchedThrough || '',
              ewayTransportMode: invoice.ewayTransportMode || '',
              destination: invoice.destination || '',
              billOfLanding: invoice.billOfLanding || '',
              motorVehicleNo: invoice.motorVehicleNo || '',
              transporterMasterId:
                invoice.transporterMasterId != null ? String(invoice.transporterMasterId) : '',
              ewayDistanceKm:
                invoice.ewayDistanceKm != null && !Number.isNaN(Number(invoice.ewayDistanceKm))
                  ? String(invoice.ewayDistanceKm)
                  : '',
              termOfDelivery: invoice.termOfDelivery || '',
              regularSupplyCode:
                String((invoice as any).supTyp ?? (invoice as any).sup_typ ?? (invoice as any).supplyType ?? '').trim() ||
                defaultRegularSupplyCode,
              status: (invoice.status as InvoiceStatus) || InvoiceStatus.DRAFT,
              invoiceDetails: invoice.invoiceDetails?.map(detail => {
                const manualFromApi = Boolean(detail.is_manual ?? detail.isManual);
                return {
                  id: detail.id,
                  endgoodId: detail.endgoodId?.toString() || detail.endgood?.id?.toString() || '',
                  uom: detail.uom || '',
                  qty: detail.qty || 0,
                  rate: detail.rate || 0,
                  hsn: detail.hsn || '',
                  cgst: detail.cgst || 0,
                  sgst: detail.sgst || 0,
                  igst: detail.igst || 0,
                  workOrderId: detail.workOrderId?.toString() || detail.workOrder?.id?.toString() || '',
                  typeOfGood: detail.typeOfGood || '',
                  boxes: detail.boxes || '',
                  selectedPackingDetailIds: detail.packingDetailIds || [],
                  heatNo: detail.heatNo || '',
                  userProvidedHeatNo: detail.userProvidedHeatNo || '',
                  amount: detail.qty * detail.rate || 0,
                  allowRateEdit: false,
                  manualQuantityRate: manualFromApi,
                  isManualEntry: manualFromApi
                };
              }) || [],
              otherCharges: invoice.otherCharges?.map((charge) => {
                const desc =
                  charge.chargeDescription ??
                  charge.chargeMaster?.description ??
                  charge.chargeMaster?.chargeName ??
                  '';
                const cm = charge.chargeMaster;
                let amount = charge.amount || 0;
                if (
                  textLooksLikeTcs(desc) ||
                  (cm &&
                    (textLooksLikeTcs(cm.chargeName) ||
                      textLooksLikeTcs(cm.description ?? '') ||
                      textLooksLikeTcs(cm.chargeCode ?? '')))
                ) {
                  amount = roundTcsToWholeRupee(amount);
                }
                return {
                  id: charge.id,
                  chargeMasterId: charge.chargeMasterId?.toString() || charge.chargeMaster?.id?.toString() || '',
                  description: desc,
                  amount,
                  rate: charge.rate || 0,
                  sequenceOrder: charge.sequenceOrder ?? 1,
                  applicableOn: (charge as any).applicableOn ?? charge.chargeMaster?.applicableOn ?? ''
                };
              }) || []
            });
          setEditLoadState('ok');
        } catch (error) {
          // console.error('Error loading invoice data:', error);
          setEditLoadState('not_found');
          toast.error('Failed to load invoice data');
        }
      }
    };

    loadInvoiceData();
  }, [invoiceId, isEdit, currentDate, defaultRegularSupplyCode, selectedBranch]);

  const isRegularSupplyWithoutTax = isSupplyTypeWithoutTax(formData.regularSupplyCode);

  const handleCreateTransporter = async () => {
    if (!newTransporter.transporterId?.trim() && !newTransporter.transporterName?.trim()) {
      toast.error('Please enter transporter name or GSTIN');
      return;
    }
    try {
      const payload = {
        transporterId: newTransporter.transporterId.trim() || undefined,
        transporterName: newTransporter.transporterName.trim() || undefined,
      };
      const res = await transporterMasterApiService.create(payload);
      if (!res.success || !res.data) {
        toast.error(res.message || 'Failed to create transporter');
        return;
      }
      const created = res.data;
      setTransporters((prev) => {
        const exists = prev.some((t) => t.id === created.id);
        return exists ? prev : [...prev, created];
      });
      setFormData((prev) => ({
        ...prev,
        transporterMasterId: String(created.id),
      }));
      toast.success('Transporter created');
      setIsTransporterDialogOpen(false);
      setNewTransporter({
        transporterId: '',
        transporterName: '',
      });
    } catch {
      toast.error('Failed to create transporter');
    }
  };

  // Fetch available boxes for all endgoods when invoice data is loaded
  useEffect(() => {
    const fetchBoxesForAllEndgoods = async () => {
      if (isEdit && formData.invoiceDetails.length > 0) {
        // Fetch available boxes for each invoice detail (with its specific invoiceDetailId)
        for (const detail of formData.invoiceDetails) {
          if (detail.endgoodId && detail.id) {
            await fetchAvailableBoxesForEndgood(detail.endgoodId, detail.id);
          }
        }
      }
    };

    fetchBoxesForAllEndgoods();
  }, [formData.invoiceDetails, isEdit]);

  // Update selected entities when IDs change
  useEffect(() => {
    if (selectedBranch) {
      const branch = branches.find(b => b.id === selectedBranch);
      setSelectedSeller(branch || null);
      // Update formData with the current branch
      setFormData(prev => ({
        ...prev,
        branchId: selectedBranch.toString()
      }));
    }
  }, [selectedBranch, branches]);

  useEffect(() => {
    if (formData.shipToId) {
      const customer = customers.find(c => c.id?.toString() === formData.shipToId);
      setSelectedShipTo(customer || null);
      
      // If "bill to same as ship to" is checked, automatically set bill to
      if (billToSameAsShipTo) {
        setFormData(prev => ({
          ...prev,
          billToId: formData.shipToId
        }));
        setSelectedBillTo(customer || null);
      }
    }
  }, [formData.shipToId, customers, billToSameAsShipTo]);

  useEffect(() => {
    if (formData.billToId) {
      const customer = customers.find(c => c.id?.toString() === formData.billToId);
      setSelectedBillTo(customer || null);
    }
  }, [formData.billToId, customers]);

  useEffect(() => {
    if (formData.salesOrderId) {
      const so = salesOrders.find(so => so.id?.toString() === formData.salesOrderId);
      setSelectedSalesOrder(so || null);
      if (so) {
        const normalizedRefDate = so.refDate ? so.refDate.split(/[T ]/)[0] : '';
        setFormData(prev => ({
          ...prev,
          billToId: so.customerId?.toString() || '',
          buyerOrderNo: so.refNo || '',
          buyerOrderDate: normalizedRefDate
        }));
        // Fetch sales order line items for rate auto-fill when selecting endgood
        salesOrderApiService.getSalesOrderDetails(formData.salesOrderId).then((res) => {
          if (res?.success && Array.isArray(res.data)) setSalesOrderDetails(res.data);
          else setSalesOrderDetails([]);
        }).catch(() => setSalesOrderDetails([]));
      } else {
        setSalesOrderDetails([]);
      }
    } else {
      setSalesOrderDetails([]);
    }
  }, [formData.salesOrderId, salesOrders]);

  // Recalculate taxes when seller or bill to changes
  useEffect(() => {
    if (selectedSeller && selectedBillTo) {
      recalculateAllTaxes();
    }
  }, [selectedSeller, selectedBillTo]);

  // Zero or restore line taxes when supply type changes (SEZWOP / EXPWOP = no tax)
  useEffect(() => {
    if (formData.invoiceDetails.length > 0) {
      recalculateAllTaxes();
    }
  }, [formData.regularSupplyCode]);

  // Recalculate computed other charges (e.g. TCS % of total) whenever invoice details change
  useEffect(() => {
    const masters = Array.isArray(chargeMasters) ? chargeMasters : [];
    setFormData(prev => {
      const subtotal = prev.invoiceDetails.reduce((sum, d) => sum + d.amount, 0);
      const totalTax = prev.invoiceDetails.reduce((s, d) => s + (d.cgst || 0) + (d.sgst || 0) + (d.igst || 0), 0);
      const baseTotal = subtotal + totalTax;
      let hasChange = false;
      const updatedCharges = prev.otherCharges.map(charge => {
        if (isChargeComputedFromTotal(charge)) {
          let newAmount = Math.round((baseTotal * charge.rate / 100) * 100) / 100;
          if (isTcsOtherCharge(charge, masters)) newAmount = roundTcsToWholeRupee(newAmount);
          if (newAmount !== charge.amount) hasChange = true;
          return { ...charge, amount: newAmount };
        }
        return charge;
      });
      if (!hasChange) return prev;
      return { ...prev, otherCharges: updatedCharges };
    });
  }, [formData.invoiceDetails, chargeMasters]);

  const handleInputChange = (field: keyof InvoiceFormData, value: string | InvoiceStatus | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDistanceChange = (value: string) => {
    if (!isValidDistanceInput(value)) return;
    handleInputChange('ewayDistanceKm', value);
  };

  const isFieldDisabled = () => false;

  const handleBillToSameAsShipToChange = (checked: boolean) => {
    setBillToSameAsShipTo(checked);
    
    if (checked && formData.shipToId) {
      // Set bill to same as ship to
      setFormData(prev => ({
        ...prev,
        billToId: formData.shipToId
      }));
      setSelectedBillTo(selectedShipTo);
    } else {
      // Clear bill to selection
      setFormData(prev => ({
        ...prev,
        billToId: ''
      }));
      setSelectedBillTo(null);
    }
  };

  // Function to determine tax type based on seller and bill to states
  const getTaxType = () => {
    if (!selectedSeller || !selectedBillTo) return 'cgst_sgst';
    
    // If seller and bill to are in different states, use IGST
    // If same state, use CGST/SGST
    return selectedSeller.stateCode === selectedBillTo.stateCode ? 'cgst_sgst' : 'igst';
  };

  // Function to get tax rates from HSN
  const getTaxRatesFromHsn = (hsn: any) => {
    if (isRegularSupplyWithoutTax) {
      return { cgst: 0, sgst: 0, igst: 0 };
    }
    if (!hsn) {
      return { cgst: 0, sgst: 0, igst: 0 };
    }

    return {
      cgst: hsn.currentCgstRate || 0,
      sgst: hsn.currentSgstRate || 0,
      igst: hsn.currentIgstRate || 0
    };
  };

  // Function to calculate tax amount for a given detail
  const calculateTaxAmount = (detail: InvoiceDetailFormData) => {
    if (isRegularSupplyWithoutTax) {
      return { cgst: 0, sgst: 0, igst: 0 };
    }
    const taxType = getTaxType();
    const hsnObj = (detail.isManualEntry || detail.isServiceEntry)
      ? hsnCodes.find(h => h.hsnCode === detail.hsn)
      : packedEndgoods.find(e => e.id?.toString() === detail.endgoodId)?.hsn;
    if (!hsnObj) {
      return {
        cgst: detail.cgst || 0,
        sgst: detail.sgst || 0,
        igst: detail.igst || 0,
      };
    }
    const taxRates = getTaxRatesFromHsn(hsnObj);
    
    if (taxType === 'cgst_sgst') {
      return {
        cgst: (detail.amount * taxRates.cgst) / 100,
        sgst: (detail.amount * taxRates.sgst) / 100,
        igst: 0
      };
    } else {
      return {
        cgst: 0,
        sgst: 0,
        igst: (detail.amount * taxRates.igst) / 100
      };
    }
  };

  // Cache key: in edit mode use endgoodId-invoiceDetailId so each detail gets its own list (with already-selected boxes)
  const getBoxCacheKey = (endgoodId: string, invoiceDetailId?: number) =>
    invoiceDetailId ? `${endgoodId}-${invoiceDetailId}` : endgoodId;

  // Function to get available box types for a specific endgood (and optional invoice detail in edit mode)
  const getAvailableBoxTypesForEndgood = (endgoodId: string, invoiceDetailId?: number): PackingDetail[] => {
    if (!endgoodId) return [];
    const key = getBoxCacheKey(endgoodId, invoiceDetailId);
    return availableBoxesPerEndgood[key] || availableBoxesPerEndgood[endgoodId] || [];
  };

  // Function to fetch available boxes for a specific endgood (called when user selects endgood)
  const fetchAvailableBoxesForEndgood = async (endgoodId: string, invoiceDetailId?: number) => {
    if (!endgoodId || !selectedBranch) return;
    const cacheKey = getBoxCacheKey(endgoodId, invoiceDetailId);
    if (availableBoxesPerEndgood[cacheKey]) return; // Already cached for this key

    try {
      // Edit mode with invoiceDetailId: use /boxes endpoint to get available + linked boxes for this invoice line
      const response =
        isEdit && invoiceDetailId
          ? await packingApiService.getBoxesForEndgood(endgoodId, selectedBranch, invoiceDetailId)
          : invoiceDetailId
            ? await packingApiService.getAvailableBoxesForEndgoodWithInvoiceDetail(endgoodId, invoiceDetailId, selectedBranch)
            : await packingApiService.getAvailableBoxesForEndgood(endgoodId, selectedBranch);

      if (response.success) {
        setAvailableBoxesPerEndgood(prev => ({
          ...prev,
          [cacheKey]: response.data || []
        }));
      }
    } catch (error) {
      // console.error('Error fetching available boxes for endgood:', error);
      // Fallback to static filtering if API fails
      const endgoodBoxTypeIds = endgoodBoxTypes
        .filter(ebt => ebt.endgoodId?.toString() === endgoodId)
        .map(ebt => ebt.boxTypeId);
      
      // Convert BoxType to PackingDetail format for fallback
      const fallbackBoxes: PackingDetail[] = boxTypes
        .filter(bt => endgoodBoxTypeIds.includes(bt.id))
        .map(bt => ({
          id: bt.id,
          headerBatchId: 0, // Default value for fallback (changed from packingHeaderId)
          boxNumber: bt.name || `Box-${bt.id}`,
          status: 'FULL' as any, // Default status
          quantity: bt.maxQuantity || 0,
          notes: `Fallback box type: ${bt.name}`,
          updatedAt: new Date().toISOString()
        }));
      
      setAvailableBoxesPerEndgood(prev => ({
        ...prev,
        [cacheKey]: fallbackBoxes
      }));
    }
  };

  // Function to handle endgood selection and auto-fill HSN (and tax rates)
  const handleEndgoodSelection = async (endgoodId: string, detailIndex: number) => {
    let endgood = packedEndgoods.find(e => e.id?.toString() === endgoodId);
    // If id-name list doesn't include hsn, fetch full endgood to get HSN and tax rates
    if (endgood && !endgood.hsn?.hsnCode) {
      try {
        const fullEndgood = await endgoodApiService.getEndgoodById(endgoodId);
        endgood = fullEndgood;
      } catch {
        // Keep existing endgood; hsn will stay empty
      }
    }
    if (endgood) {
      const taxType = getTaxType();
      const taxRates = getTaxRatesFromHsn(endgood.hsn);
      // Auto-fill rate: sales order line first, else endgood cost API
      let rate = 0;
      const soDetail = salesOrderDetails.find((d) => d.endgoodId === parseInt(endgoodId, 10));
      if (soDetail != null) {
        rate = soDetail.unitPrice ?? 0;
      } else if (formData.invoiceDate && selectedBranch != null) {
        try {
          const costRes = await endgoodCostMasterApiService.getCostAsOf(
            endgoodId,
            formData.invoiceDate,
            selectedBranch
          );
          if (costRes?.success && costRes.data != null) {
            rate = Number(costRes.data.cost ?? 0);
          }
        } catch {
          // Keep rate 0 on cost API failure
        }
      }

      // Fetch available boxes for this endgood
      fetchAvailableBoxesForEndgood(endgoodId);

      setFormData(prev => ({
        ...prev,
        invoiceDetails: prev.invoiceDetails.map((detail, index) =>
          index === detailIndex
            ? {
                ...detail,
                endgoodId,
                hsn: endgood!.hsn?.hsnCode || '',
                rate,
                cgst: 0,
                sgst: 0,
                igst: 0,
                selectedPackingDetailIds: [], // Reset selected packing detail IDs when endgood changes
                boxes: '', // Reset boxes when endgood changes
                qty: 0, // Reset quantity when endgood changes
                amount: 0
              }
            : detail
        )
      }));
      // Keep packedEndgoods in sync so tax calculation (from HSN) works when boxes are selected
      if (endgood?.hsn?.hsnCode) {
        setPackedEndgoods(prev => prev.map(e => (e.id?.toString() === endgoodId ? endgood! : e)));
      }
    }
  };

  // Function to handle multi-select box types and calculate total quantity
  // Handle "Select All" for packing details
  const handleSelectAllPackingDetails = (detailIndex: number) => {
    const currentDetail = formData.invoiceDetails[detailIndex];
    const availableBoxes = getAvailableBoxTypesForEndgood(currentDetail.endgoodId, currentDetail.id);
    const allPackingDetailIds = availableBoxes.map(pd => pd.id || 0);
    handlePackingDetailMultiSelect(allPackingDetailIds, detailIndex);
  };

  // True if this charge amount is computed from (subtotal + tax) * rate% (e.g. TCS, or any non-MANUAL with rate)
  const isChargeComputedFromTotal = (charge: OtherChargeFormData): boolean => {
    const applicableOn = (charge.applicableOn ?? '').trim().toUpperCase();
    if (applicableOn === 'MANUAL') return false;
    return charge.rate != null && charge.rate !== 0;
  };

  const recalcOtherChargesFromDetails = (details: InvoiceDetailFormData[], charges: OtherChargeFormData[]) => {
    const masters = Array.isArray(chargeMasters) ? chargeMasters : [];
    const subtotal = details.reduce((sum, d) => sum + d.amount, 0);
    const totalTax = details.reduce((s, d) => s + (d.cgst || 0) + (d.sgst || 0) + (d.igst || 0), 0);
    const baseTotal = subtotal + totalTax;
    return charges.map(charge => {
      if (isChargeComputedFromTotal(charge)) {
        let raw = Math.round((baseTotal * charge.rate / 100) * 100) / 100;
        if (isTcsOtherCharge(charge, masters)) raw = roundTcsToWholeRupee(raw);
        return { ...charge, amount: raw };
      }
      return charge;
    });
  };

  const handlePackingDetailMultiSelect = async (packingDetailIds: number[], detailIndex: number) => {
    const currentDetail = formData.invoiceDetails[detailIndex];
    if (!currentDetail?.endgoodId) return;

    const availableBoxes = getAvailableBoxTypesForEndgood(currentDetail.endgoodId, currentDetail.id);
    let totalQuantity = 0;
    let boxesDescription = '';
    const qtyCounts = new Map<number, number>();

    packingDetailIds.forEach((packingDetailId, index) => {
      const packingDetail = availableBoxes.find(pd => pd.id === packingDetailId);
      if (!packingDetail) return;
      const qty = packingDetail.quantity ?? 0;
      totalQuantity += qty;
      qtyCounts.set(qty, (qtyCounts.get(qty) ?? 0) + 1);
    });

    if (packingDetailIds.length > 0) {
      const inner = Array.from(qtyCounts.entries())
        .sort(([qtyA], [qtyB]) => qtyA - qtyB)
        .map(([qty, count]) => `${count} X ${Number.isInteger(qty) ? qty : qty}`)
        .join(', ');
      boxesDescription = `${packingDetailIds.length} BOX( ${inner} )`;
    }

    const newAmount = totalQuantity * currentDetail.rate;
    let endgood = packedEndgoods.find(e => e.id?.toString() === currentDetail.endgoodId);
    if (endgood && !endgood.hsn?.hsnCode) {
      try {
        const fullEndgood = await endgoodApiService.getEndgoodById(currentDetail.endgoodId);
        endgood = fullEndgood;
        setPackedEndgoods(prev => prev.map(e => (e.id?.toString() === currentDetail.endgoodId ? fullEndgood : e)));
      } catch {
        // keep existing endgood
      }
    }
    const taxRates = getTaxRatesFromHsn(endgood?.hsn);
    const taxType = getTaxType();
    const taxAmounts =
      taxType === 'cgst_sgst'
        ? {
            cgst: (newAmount * taxRates.cgst) / 100,
            sgst: (newAmount * taxRates.sgst) / 100,
            igst: 0
          }
        : {
            cgst: 0,
            sgst: 0,
            igst: (newAmount * taxRates.igst) / 100
          };

    setFormData(prev => {
      const updatedDetails = prev.invoiceDetails.map((detail, index) =>
        index === detailIndex
          ? {
              ...detail,
              selectedPackingDetailIds: packingDetailIds,
              qty: totalQuantity,
              boxes: boxesDescription,
              amount: newAmount,
              cgst: taxAmounts.cgst,
              sgst: taxAmounts.sgst,
              igst: taxAmounts.igst
            }
          : detail
      );
      const updatedCharges = recalcOtherChargesFromDetails(updatedDetails, prev.otherCharges);
      return { ...prev, invoiceDetails: updatedDetails, otherCharges: updatedCharges };
    });

    // Run recalc again after commit so Other Charges UI reliably updates
    queueMicrotask(() => {
      const masters = Array.isArray(chargeMasters) ? chargeMasters : [];
      setFormData(prev => {
        const subtotal = prev.invoiceDetails.reduce((sum, d) => sum + d.amount, 0);
        const totalTax = prev.invoiceDetails.reduce((s, d) => s + (d.cgst || 0) + (d.sgst || 0) + (d.igst || 0), 0);
        const baseTotal = subtotal + totalTax;
        let hasChange = false;
        const updatedCharges = prev.otherCharges.map(charge => {
          if (isChargeComputedFromTotal(charge)) {
            let newAmt = Math.round((baseTotal * charge.rate / 100) * 100) / 100;
            if (isTcsOtherCharge(charge, masters)) newAmt = roundTcsToWholeRupee(newAmt);
            if (newAmt !== charge.amount) hasChange = true;
            return { ...charge, amount: newAmt };
          }
          return charge;
        });
        if (!hasChange) return prev;
        return { ...prev, otherCharges: updatedCharges };
      });
    });
  };

  // Open box selection dialog and initialize from current selection
  const openBoxSelectionDialog = (detailIndex: number) => {
    const detail = formData.invoiceDetails[detailIndex];
    if (!detail?.endgoodId) return;
    setBoxSelectedIds(Array.from(new Set(detail.selectedPackingDetailIds)));
    setBoxAutoSelectCount(0);
    setBoxSelectionDetailIndex(detailIndex);
  };

  // Apply box selection: either first N boxes (capped at available) or checked boxes
  const applyBoxSelection = () => {
    if (boxSelectionDetailIndex === null) return;
    const detail = formData.invoiceDetails[boxSelectionDetailIndex];
    const available = detail?.endgoodId ? getAvailableBoxTypesForEndgood(detail.endgoodId, detail.id) : [];
    let ids: number[] = [];
    if (boxAutoSelectCount > 0 && available.length > 0) {
      // Auto-select N boxes: first N from list, never more than available
      const n = Math.min(boxAutoSelectCount, available.length);
      ids = available.slice(0, n).map((pd) => pd.id ?? 0);
    } else {
      ids = [...boxSelectedIds];
    }
    handlePackingDetailMultiSelect(ids, boxSelectionDetailIndex);
    setBoxSelectionDetailIndex(null);
    setBoxAutoSelectCount(0);
    setBoxSelectedIds([]);
  };

  const toggleBoxSelectedInDialog = (id: number) => {
    setBoxSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // When user updates the number input, auto-select first N boxes so input and checkboxes stay in sync
  useEffect(() => {
    if (boxSelectionDetailIndex === null) return;
    const detail = formData.invoiceDetails[boxSelectionDetailIndex];
    if (!detail?.endgoodId) return;
    const available = getAvailableBoxTypesForEndgood(detail.endgoodId, detail.id);
    if (boxAutoSelectCount > 0 && available.length > 0) {
      const n = Math.min(boxAutoSelectCount, available.length);
      setBoxSelectedIds(available.slice(0, n).map((pd) => pd.id ?? 0));
    }
  }, [boxAutoSelectCount, boxSelectionDetailIndex, formData.invoiceDetails, availableBoxesPerEndgood]);

  // Function to toggle packing detail selection
  const togglePackingDetailSelection = (packingDetailId: number, detailIndex: number) => {
    const currentDetail = formData.invoiceDetails[detailIndex];
    if (!currentDetail) return;

    const currentSelection = currentDetail.selectedPackingDetailIds || [];
    let newSelection: number[];

    if (currentSelection.includes(packingDetailId)) {
      // Remove from selection
      newSelection = currentSelection.filter(id => id !== packingDetailId);
    } else {
      // Add to selection
      newSelection = [...currentSelection, packingDetailId];
    }

    handlePackingDetailMultiSelect(newSelection, detailIndex);
  };

  const addInvoiceDetail = () => {
    const newDetail: InvoiceDetailFormData = {
      id: 0, // Use 0 to indicate new detail (not yet saved to backend)
      endgoodId: '',
      uom: '',
      qty: 0,
      rate: 0,
      hsn: '',
      cgst: 0,
      sgst: 0,
      igst: 0,
      workOrderId: '',
      typeOfGood: '',
      boxes: '',
      selectedPackingDetailIds: [],
      heatNo: '',
      userProvidedHeatNo: '',
      amount: 0,
      allowRateEdit: false,
      manualQuantityRate: false
    };
    setFormData(prev => ({
      ...prev,
      invoiceDetails: [...prev.invoiceDetails, newDetail]
    }));
  };

  const addManualEntryDetail = () => {
    const newDetail: InvoiceDetailFormData = {
      id: 0,
      endgoodId: '',
      uom: 'Nos',
      qty: 0,
      rate: 0,
      hsn: '',
      cgst: 0,
      sgst: 0,
      igst: 0,
      workOrderId: '',
      typeOfGood: '',
      boxes: '',
      selectedPackingDetailIds: [],
      heatNo: '',
      userProvidedHeatNo: '',
      amount: 0,
      manualQuantityRate: true,
      isManualEntry: true,
      manualDescription: ''
    };
    setFormData(prev => ({
      ...prev,
      invoiceDetails: [...prev.invoiceDetails, newDetail]
    }));
  };

  const addServiceEntryDetail = () => {
    const newDetail: InvoiceDetailFormData = {
      id: 0,
      endgoodId: '',
      uom: '',
      qty: 0,
      rate: 0,
      hsn: '',
      cgst: 0,
      sgst: 0,
      igst: 0,
      workOrderId: '',
      typeOfGood: '',
      boxes: '',
      selectedPackingDetailIds: [],
      heatNo: '',
      userProvidedHeatNo: '',
      amount: 0,
      manualQuantityRate: true,
      isServiceEntry: true,
      manualDescription: ''
    };
    setFormData(prev => ({
      ...prev,
      invoiceDetails: [...prev.invoiceDetails, newDetail]
    }));
  };

  const updateInvoiceDetail = (index: number, field: keyof InvoiceDetailFormData, value: any) => {
    setFormData(prev => {
      const updatedDetails = [...prev.invoiceDetails];
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };

      if (field === 'qty' || field === 'rate') {
        const detail = updatedDetails[index];
        if (!detail.isServiceEntry) {
          const qty = field === 'qty' ? value : detail.qty;
          const rate = field === 'rate' ? value : detail.rate;
          updatedDetails[index].amount = qty * rate;
        }
        const taxAmounts = calculateTaxAmount(updatedDetails[index]);
        updatedDetails[index].cgst = taxAmounts.cgst;
        updatedDetails[index].sgst = taxAmounts.sgst;
        updatedDetails[index].igst = taxAmounts.igst;
      }
      if (field === 'amount' && updatedDetails[index].isServiceEntry) {
        const taxAmounts = calculateTaxAmount(updatedDetails[index]);
        updatedDetails[index].cgst = taxAmounts.cgst;
        updatedDetails[index].sgst = taxAmounts.sgst;
        updatedDetails[index].igst = taxAmounts.igst;
      }
      if (field === 'hsn' && (updatedDetails[index].isManualEntry || updatedDetails[index].isServiceEntry)) {
        const taxAmounts = calculateTaxAmount(updatedDetails[index]);
        updatedDetails[index].cgst = taxAmounts.cgst;
        updatedDetails[index].sgst = taxAmounts.sgst;
        updatedDetails[index].igst = taxAmounts.igst;
      }

      const updatedCharges = recalcOtherChargesFromDetails(updatedDetails, prev.otherCharges);
      return { ...prev, invoiceDetails: updatedDetails, otherCharges: updatedCharges };
    });
  };

  const toggleRateEdit = (index: number) => {
    setFormData(prev => {
      const updatedDetails = [...prev.invoiceDetails];
      const detail = updatedDetails[index];
      if (!detail || detail.isManualEntry || detail.isServiceEntry) return prev;
      updatedDetails[index] = {
        ...detail,
        allowRateEdit: !detail.allowRateEdit,
      };
      return { ...prev, invoiceDetails: updatedDetails };
    });

    setRateDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const removeInvoiceDetail = (index: number) => {
    setFormData(prev => ({
      ...prev,
      invoiceDetails: prev.invoiceDetails.filter((_, i) => i !== index)
    }));
  };

  const addOtherCharge = () => {
    const newCharge: OtherChargeFormData = {
      id: 0, // Use 0 to indicate new charge (not yet saved to backend)
      chargeMasterId: '', // User will select from dropdown
      description: '',
      amount: 0,
      rate: 0,
      sequenceOrder: formData.otherCharges.length + 1,
      applicableOn: ''
    };
    setFormData(prev => ({
      ...prev,
      otherCharges: [...prev.otherCharges, newCharge]
    }));
  };

  const updateOtherCharge = (index: number, field: keyof OtherChargeFormData, value: any) => {
    setFormData(prev => {
      const updatedCharges = [...prev.otherCharges];
      updatedCharges[index] = { ...updatedCharges[index], [field]: value };
      return { ...prev, otherCharges: updatedCharges };
    });
  };

  const applyChargeMasterToOtherCharge = (index: number, cm: ChargeMaster) => {
    setFormData(prev => {
      const subtotal = prev.invoiceDetails.reduce((sum, d) => sum + d.amount, 0);
      const totalTax = prev.invoiceDetails.reduce((s, d) => s + d.cgst + d.sgst + d.igst, 0);
      const rate = cm.defaultRate ?? 0;
      const applicableOn = (cm.applicableOn ?? '').trim().toUpperCase();
      const isTotal = applicableOn === 'TOTAL';
      const isPercentage = (cm.defaultAmountType || '').toLowerCase() === 'percentage';
      const baseTotal = subtotal + totalTax;
      let amount = 0;
      if (isTotal && rate !== 0) {
        amount = Math.round((baseTotal * rate / 100) * 100) / 100;
      } else if (isPercentage && rate !== 0) {
        amount = Math.round((subtotal * rate / 100) * 100) / 100;
      }
      if (textLooksLikeTcs(cm.chargeName) || textLooksLikeTcs(cm.description) || textLooksLikeTcs(cm.chargeCode)) {
        amount = roundTcsToWholeRupee(amount);
      }
      const updatedCharges = [...prev.otherCharges];
      updatedCharges[index] = {
        ...updatedCharges[index],
        description: cm.description ?? '',
        rate,
        amount,
        applicableOn: cm.applicableOn ?? '',
      };
      return { ...prev, otherCharges: updatedCharges };
    });
  };

  const removeOtherCharge = (index: number) => {
    setFormData(prev => ({
      ...prev,
      otherCharges: prev.otherCharges.filter((_, i) => i !== index)
    }));
  };

  const calculateSubtotal = () => {
    return formData.invoiceDetails.reduce((sum, detail) => sum + detail.amount, 0);
  };

  const calculateTotalCGST = () => {
    if (isRegularSupplyWithoutTax) return 0;
    const taxType = getTaxType();
    if (taxType === 'igst') return 0; // No CGST for inter-state
    
    return formData.invoiceDetails.reduce((sum, detail) => sum + detail.cgst, 0);
  };

  const calculateTotalSGST = () => {
    if (isRegularSupplyWithoutTax) return 0;
    const taxType = getTaxType();
    if (taxType === 'igst') return 0; // No SGST for inter-state
    
    return formData.invoiceDetails.reduce((sum, detail) => sum + detail.sgst, 0);
  };

  const calculateTotalIGST = () => {
    if (isRegularSupplyWithoutTax) return 0;
    const taxType = getTaxType();
    if (taxType === 'cgst_sgst') return 0; // No IGST for intra-state
    
    return formData.invoiceDetails.reduce((sum, detail) => sum + detail.igst, 0);
  };

  // Function to recalculate all taxes when tax type changes (and computed other charges e.g. TCS)
  const recalculateAllTaxes = () => {
    setFormData(prev => {
      const updatedDetails = prev.invoiceDetails.map(detail => {
        const taxAmounts = calculateTaxAmount(detail);
        return {
          ...detail,
          cgst: taxAmounts.cgst,
          sgst: taxAmounts.sgst,
          igst: taxAmounts.igst
        };
      });
      const updatedCharges = recalcOtherChargesFromDetails(updatedDetails, prev.otherCharges);
      return { ...prev, invoiceDetails: updatedDetails, otherCharges: updatedCharges };
    });
  };

  const getEffectiveOtherChargeAmount = (charge: OtherChargeFormData): number => {
    const masters = Array.isArray(chargeMasters) ? chargeMasters : [];
    let raw: number;
    if (isChargeComputedFromTotal(charge)) {
      const subtotal = calculateSubtotal();
      const totalTax = calculateTotalCGST() + calculateTotalSGST() + calculateTotalIGST();
      const baseTotal = subtotal + totalTax;
      raw = Math.round((baseTotal * charge.rate / 100) * 100) / 100;
    } else {
      raw = charge.amount;
    }
    if (isTcsOtherCharge(charge, masters)) return roundTcsToWholeRupee(raw);
    return raw;
  };

  const calculateOtherChargesTotal = () => {
    return formData.otherCharges.reduce((sum, charge) => sum + getEffectiveOtherChargeAmount(charge), 0);
  };

  const calculateGrandTotal = () => {
    return calculateSubtotal() + calculateTotalCGST() + calculateTotalSGST() + calculateTotalIGST() + calculateOtherChargesTotal();
  };

  const totalTaxAmount = () => calculateTotalCGST() + calculateTotalSGST() + calculateTotalIGST();

  const hsnWiseTaxDetails = useMemo((): HsnWiseRow[] => {
    const details = formData.invoiceDetails.filter((d) => (d.endgoodId || d.isManualEntry || d.isServiceEntry) && d.amount > 0);
    const byHsn: Record<string, { taxableValue: number; cgstAmount: number; sgstAmount: number; igstAmount: number; cgstRate: number; sgstRate: number; igstRate: number }> = {};
    details.forEach((d) => {
      const hsn = d.hsn || 'N/A';
      if (!byHsn[hsn]) {
        const rates = (d.isManualEntry || d.isServiceEntry)
          ? getTaxRatesFromHsn(hsnCodes.find((h) => h.hsnCode === d.hsn))
          : getTaxRatesFromHsn(packedEndgoods.find((e) => e.id?.toString() === d.endgoodId)?.hsn);
        byHsn[hsn] = {
          taxableValue: 0,
          cgstAmount: 0,
          sgstAmount: 0,
          igstAmount: 0,
          cgstRate: rates.cgst,
          sgstRate: rates.sgst,
          igstRate: rates.igst
        };
      }
      byHsn[hsn].taxableValue += d.amount;
      byHsn[hsn].cgstAmount += d.cgst || 0;
      byHsn[hsn].sgstAmount += d.sgst || 0;
      byHsn[hsn].igstAmount += d.igst || 0;
    });
    return Object.entries(byHsn).map(([hsn, v]) => {
      // When HSN rate is 0 but we have tax amount (e.g. edit mode where endgood HSN wasn't loaded), derive rate from amount
      const tv = v.taxableValue;
      const deriveRate = (amount: number, rate: number) =>
        tv > 0 && amount > 0 && (rate == null || rate === 0) ? (amount / tv) * 100 : rate;
      const cgstRate = isRegularSupplyWithoutTax ? 0 : deriveRate(v.cgstAmount, v.cgstRate);
      const sgstRate = isRegularSupplyWithoutTax ? 0 : deriveRate(v.sgstAmount, v.sgstRate);
      const igstRate = isRegularSupplyWithoutTax ? 0 : deriveRate(v.igstAmount, v.igstRate);
      return {
        hsn,
        taxableValue: tv,
        cgstRate,
        cgstAmount: v.cgstAmount,
        sgstRate,
        sgstAmount: v.sgstAmount,
        igstRate,
        igstAmount: v.igstAmount,
        totalTaxAmount: v.cgstAmount + v.sgstAmount + v.igstAmount
      };
    });
  }, [formData.invoiceDetails, formData.regularSupplyCode, packedEndgoods, hsnCodes, isRegularSupplyWithoutTax]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBranch || !formData.shipToId || (!billToSameAsShipTo && !formData.billToId)) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.invoiceDetails.length === 0) {
      alert('Please add at least one invoice detail');
      return;
    }

    // Validate that all other charges have a selected charge type
    const invalidCharges = formData.otherCharges.filter(charge => !charge.chargeMasterId || charge.chargeMasterId === '');
    if (invalidCharges.length > 0) {
      toast.error('Please select a charge type for all other charges before saving.');
      return;
    }

    const grandTotalRounded = Math.round(calculateGrandTotal() * 100) / 100;
    if (grandTotalRounded <= 0) {
      toast.error('Cannot save invoice: total amount must be greater than zero.');
      return;
    }

    try {
      setLoading(true);

      // Resolve tax for any detail with amount but 0 tax (e.g. backend endgood lacked HSN)
      const taxType = getTaxType();
      const resolveDetailTax = async (detail: InvoiceDetailFormData): Promise<{ cgst: number; sgst: number; igst: number }> => {
        if (isRegularSupplyWithoutTax) return { cgst: 0, sgst: 0, igst: 0 };
        const totalTax = (detail.cgst || 0) + (detail.sgst || 0) + (detail.igst || 0);
        if (detail.amount <= 0 || totalTax > 0) {
          return { cgst: detail.cgst || 0, sgst: detail.sgst || 0, igst: detail.igst || 0 };
        }
        if (detail.isManualEntry || detail.isServiceEntry) {
          const hsnObj = hsnCodes.find(h => h.hsnCode === detail.hsn);
          const rates = getTaxRatesFromHsn(hsnObj);
          if (taxType === 'cgst_sgst') {
            return { cgst: (detail.amount * rates.cgst) / 100, sgst: (detail.amount * rates.sgst) / 100, igst: 0 };
          }
          return { cgst: 0, sgst: 0, igst: (detail.amount * rates.igst) / 100 };
        }
        let endgood = packedEndgoods.find(e => e.id?.toString() === detail.endgoodId);
        if (endgood && !endgood.hsn?.hsnCode && detail.endgoodId) {
          try {
            endgood = await endgoodApiService.getEndgoodById(detail.endgoodId);
          } catch {
            // keep existing endgood
          }
        }
        const rates = getTaxRatesFromHsn(endgood?.hsn);
        if (taxType === 'cgst_sgst') {
          return {
            cgst: (detail.amount * rates.cgst) / 100,
            sgst: (detail.amount * rates.sgst) / 100,
            igst: 0
          };
        }
        return {
          cgst: 0,
          sgst: 0,
          igst: (detail.amount * rates.igst) / 100
        };
      };

      const resolvedTaxes = await Promise.all(formData.invoiceDetails.map(d => resolveDetailTax(d)));

      // Prepare invoice details with resolved tax
      const invoiceDetails = formData.invoiceDetails.map((detail, i) => {
        const tax = resolvedTaxes[i];
        const detailData: any = {
          invoiceHeaderId: 0, // Will be set by backend
          endgoodId: detail.endgoodId ? parseInt(detail.endgoodId) : undefined,
          uom: detail.uom || undefined,
          qty: detail.qty,
          rate: detail.rate,
          hsn: detail.hsn || undefined,
          cgst: tax.cgst,
          sgst: tax.sgst,
          igst: tax.igst,
          workOrderId: detail.workOrderId ? parseInt(detail.workOrderId) : undefined,
          typeOfGood: detail.typeOfGood || undefined,
          boxes: detail.boxes || undefined,
          packingDetailIds: detail.selectedPackingDetailIds.length > 0 ? detail.selectedPackingDetailIds : undefined,
          heatNo: detail.heatNo || undefined,
          userProvidedHeatNo: detail.userProvidedHeatNo || undefined,
          isManual: Boolean(detail.isManualEntry),
          is_manual: Boolean(detail.isManualEntry)
        };
        
        // Only include ID for existing details (not for new ones)
        // For new details, let the backend generate the ID
        if (isEdit && detail.id > 0) {
          detailData.id = detail.id;
        }
        
        return detailData;
      });

      const grandTotalTaxResolved = resolvedTaxes.reduce((s, t) => s + t.cgst + t.sgst + t.igst, 0);
      const finalGrandRounded =
        Math.round((calculateSubtotal() + grandTotalTaxResolved + calculateOtherChargesTotal()) * 100) / 100;
      if (finalGrandRounded <= 0) {
        toast.error('Cannot save invoice: total amount must be greater than zero.');
        return;
      }

      // Other charges are saved via a separate API after invoice save (see below)
      const otherChargesForPayload = formData.otherCharges
        .filter(charge => charge.chargeMasterId && charge.chargeMasterId !== '')
        .map(charge => ({
          invoiceId: 0,
          chargeMasterId: parseInt(charge.chargeMasterId),
          amount: getEffectiveOtherChargeAmount(charge),
          rate: charge.rate,
          sequenceOrder: charge.sequenceOrder
        }));

      const selectedTransporter = transporters.find(
        (t) => String(t.id) === formData.transporterMasterId
      );
      const selectedTransporterName = selectedTransporter?.transporterName || undefined;
      const selectedTransporterGstin = selectedTransporter?.transporterId || undefined;
      const trimmedEwayDistanceKm = formData.ewayDistanceKm.trim();
      if (trimmedEwayDistanceKm !== '' && !isValidDistanceInput(trimmedEwayDistanceKm)) {
        toast.error('Distance must be a number with at most 1 decimal place.');
        return;
      }
      const parsedEwayDistanceKm =
        trimmedEwayDistanceKm !== '' ? Number(trimmedEwayDistanceKm) : undefined;
      const ewayTransporterPayload =
        selectedTransporterGstin || selectedTransporterName
          ? {
              ewayTransporterId: selectedTransporterGstin,
              ewayTransporterName: selectedTransporterName,
              eway_transporter_id: selectedTransporterGstin,
              eway_transporter_name: selectedTransporterName,
            }
          : {};

      const requestData: CompleteInvoiceCreateRequest = {
        branchId: selectedBranch,
        shipTo: parseInt(formData.shipToId),
        billTo: billToSameAsShipTo ? parseInt(formData.shipToId) : parseInt(formData.billToId),
        createDate: currentDate,
        createdBy: 1, // Hardcoded as requested
        invoiceNo: formData.invoiceNo,
        invoiceDate: currentDate,
        deliverNote: formData.deliverNote,
        referenceNoAndDate: formData.referenceNoAndDate,
        otherReferences: formData.otherReferences,
        additionalRemarks: formData.additionalRemarks || undefined,
        additional_remarks: formData.additionalRemarks || undefined,
        buyerOrderNo: formData.buyerOrderNo,
        buyerOrderDate: normalizeDateOnly(formData.buyerOrderDate),
        dispatchDocNo: formData.dispatchDocNo,
        deliverNoteDate: normalizeDateOnly(formData.deliverNoteDate),
        dispatchedThrough: formData.dispatchedThrough,
        ewayTransportMode: formData.ewayTransportMode || undefined,
        destination: formData.destination,
        billOfLanding: formData.billOfLanding,
        motorVehicleNo: formData.motorVehicleNo,
        ...(formData.salesOrderId
          ? { salesOrderId: parseInt(formData.salesOrderId, 10) }
          : {}),
        ...(formData.transporterMasterId
          ? { transporterMasterId: parseInt(formData.transporterMasterId, 10) }
          : {}),
        ...(parsedEwayDistanceKm != null && !Number.isNaN(parsedEwayDistanceKm)
          ? { ewayDistanceKm: parsedEwayDistanceKm }
          : {}),
        ...ewayTransporterPayload,
        termOfDelivery: formData.termOfDelivery,
        supTyp: formData.regularSupplyCode || undefined,
        supplyType: formData.regularSupplyCode || undefined,
        grandTotal: calculateSubtotal() + grandTotalTaxResolved + calculateOtherChargesTotal(),
        grandTotalTax: grandTotalTaxResolved,
        totalAmount: calculateSubtotal(),
        status: formData.status,
        invoiceDetails,
        otherCharges: otherChargesForPayload
      };

      let response;
      if (isEdit && invoiceId) {
        const updateData: CompleteInvoiceUpdateRequest = {
          id: parseInt(invoiceId),
          branchId: selectedBranch,
          shipTo: parseInt(formData.shipToId),
          billTo: billToSameAsShipTo ? parseInt(formData.shipToId) : parseInt(formData.billToId),
          createDate: originalCreateDate || undefined,
          createdBy: 1,
          invoiceNo: formData.invoiceNo,
          invoiceDate: normalizeDateOnly(formData.invoiceDate) || undefined,
          deliverNote: formData.deliverNote,
          referenceNoAndDate: formData.referenceNoAndDate,
          otherReferences: formData.otherReferences,
          additionalRemarks: formData.additionalRemarks || undefined,
          additional_remarks: formData.additionalRemarks || undefined,
          buyerOrderNo: formData.buyerOrderNo,
          buyerOrderDate: normalizeDateOnly(formData.buyerOrderDate),
          dispatchDocNo: formData.dispatchDocNo,
          deliverNoteDate: normalizeDateOnly(formData.deliverNoteDate),
          dispatchedThrough: formData.dispatchedThrough,
          ewayTransportMode: formData.ewayTransportMode || undefined,
          destination: formData.destination,
          billOfLanding: formData.billOfLanding,
          motorVehicleNo: formData.motorVehicleNo,
          ...(formData.salesOrderId
            ? { salesOrderId: parseInt(formData.salesOrderId, 10) }
            : {}),
          ...(formData.transporterMasterId
            ? { transporterMasterId: parseInt(formData.transporterMasterId, 10) }
            : {}),
          ...(parsedEwayDistanceKm != null && !Number.isNaN(parsedEwayDistanceKm)
            ? { ewayDistanceKm: parsedEwayDistanceKm }
            : {}),
          ...ewayTransporterPayload,
          termOfDelivery: formData.termOfDelivery,
          supTyp: formData.regularSupplyCode || undefined,
          supplyType: formData.regularSupplyCode || undefined,
          grandTotal: calculateSubtotal() + grandTotalTaxResolved + calculateOtherChargesTotal(),
          grandTotalTax: grandTotalTaxResolved,
          totalAmount: calculateSubtotal(),
          status: formData.status,
          invoiceDetails: invoiceDetails as any, // Type assertion for update request
          otherCharges: otherChargesForPayload as any // Type assertion for update request
        };
        response = await invoiceApiService.updateCompleteInvoice(invoiceId, updateData);
      } else {
        response = await invoiceApiService.createCompleteInvoice(requestData);
      }

      if (response.success) {
        const action = isEdit ? 'updated' : 'created';
        toast.success(`Invoice ${action} successfully!`);
        const savedInvoiceId = response.data?.id ?? (isEdit && invoiceId ? parseInt(invoiceId, 10) : undefined);
        if (savedInvoiceId != null && !isNaN(savedInvoiceId)) {
          navigate(`/invoices/${savedInvoiceId}/view`);
        } else {
          navigate('/invoices');
        }
      } else {
        const action = isEdit ? 'updating' : 'creating';
        alert(`Error ${action} invoice: ${response.message}`);
      }
    } catch (error) {
      // console.error('Error saving invoice:', error);
      const action = isEdit ? 'updating' : 'creating';
      alert(`Error ${action} invoice. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  if ((isEdit && editLoadState === 'loading') || (!isEdit && loading)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isEdit && editLoadState === 'not_found') {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Button
          variant="outline"
          onClick={() => navigate('/invoices')}
          className="border-primary-200 text-primary-700 hover:bg-primary-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Invoices
        </Button>
        <Card className="border-primary-200 shadow-md">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">Invoice not found</p>
            <p className="text-sm text-gray-500 mt-2">
              This invoice does not exist or is not available for the selected branch.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/invoices')}
            className="border-primary-200 text-primary-700 hover:bg-primary-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEdit ? 'Edit Tax Invoice' : 'Create Tax Invoice'}
            </h1>
            <p className="text-gray-600">
              {isEdit ? 'Edit invoice details' : 'Create a new tax invoice for your customer'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tax Invoice Header */}
        <Card className="border-2 border-primary-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-t-xl">
            <CardTitle className="text-2xl font-bold text-primary-800 text-center">TAX INVOICE</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Seller Information */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Building className="h-5 w-5 text-primary-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Seller Information</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Company (Current Branch)</Label>
                    {selectedSeller ? (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                        <div className="space-y-2 text-sm">
                          <div className="font-medium text-gray-900">{selectedSeller.branchName}</div>
                          <div className="text-gray-600">{selectedSeller.address}</div>
                          <div className="text-gray-600">GSTIN: {selectedSeller.gstin}</div>
                          <div className="text-gray-600">State: {selectedSeller.stateName}</div>
                          <div className="text-gray-600">PAN: {selectedSeller.panNo}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                        <div className="text-sm text-yellow-800">
                          No branch selected. Please select a branch from the header.
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="regularSupplyCode">Regular Supply</Label>
                    <Select
                      value={formData.regularSupplyCode || defaultRegularSupplyCode}
                      onValueChange={(value) => handleInputChange('regularSupplyCode', value)}
                    >
                      <SelectTrigger id="regularSupplyCode" className="border border-gray-300 rounded-xl">
                        <SelectValue placeholder="Select supply type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(regularSupplyOptions.length > 0
                          ? regularSupplyOptions
                          : [{ code: 'B2B', name: 'Business to Business' }]).map((opt) => (
                          <SelectItem key={opt.code} value={opt.code}>
                            {opt.code} — {opt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(() => {
                      const selected = regularSupplyOptions.find(
                        (o) =>
                          String(o.code).toUpperCase() ===
                          String(formData.regularSupplyCode || '').toUpperCase()
                      );
                      const desc = selected?.description?.trim();
                      if (!desc) return null;
                      return <p className="text-xs text-gray-500 leading-snug">{desc}</p>;
                    })()}
                    {isRegularSupplyWithoutTax ? (
                      <p className="text-xs text-amber-700 leading-snug">
                        Supply without payment of tax selected (SEZWOP / EXPWOP): line taxes are calculated as 0.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Consignee & Buyer */}
              <div className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Package className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-gray-800">Consignee (Ship to)</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="shipToId">Customer *</Label>
                    <Select
                      value={formData.shipToId}
                      onValueChange={(value) => handleInputChange('shipToId', value)}
                    >
                      <SelectTrigger className="border border-gray-300 rounded-xl">
                        <SelectValue placeholder="Select Customer">
                          {customers.find((c) => c.id?.toString() === formData.shipToId)?.customerName}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContentWithSearch
                        className="min-w-[min(100vw-2rem,28rem)] max-w-[min(100vw-2rem,28rem)]"
                        searchPlaceholder="Search customer..."
                        debounceMs={300}
                      >
                        {customers.map((customer) => (
                          <SelectItem
                            key={customer.id}
                            value={customer.id?.toString() || ''}
                            textValue={customerSelectSearchText(customer)}
                            className="items-start py-2"
                          >
                            <CustomerSelectOptionLabel customer={customer} />
                          </SelectItem>
                        ))}
                      </SelectContentWithSearch>
                    </Select>
                  </div>

                  {selectedShipTo && (
                    <div className="space-y-1 text-sm">
                      {selectedShipTo.address ? (
                        <div className="text-gray-600">{selectedShipTo.address}</div>
                      ) : null}
                      <div className="text-gray-600">Vendor Code: {selectedShipTo.customerCode}</div>
                      <div className="text-gray-600">GSTIN: {selectedShipTo.gstNo}</div>
                      <div className="text-gray-600">{selectedShipTo.stateName}, Code: {selectedShipTo.stateCode}</div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <User className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-gray-800">Buyer (Bill to)</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="billToSameAsShipTo"
                        checked={billToSameAsShipTo}
                        onCheckedChange={handleBillToSameAsShipToChange}
                      />
                      <Label htmlFor="billToSameAsShipTo" className="text-sm font-medium">
                        Bill to same as Ship to
                      </Label>
                    </div>
                    
                    {!billToSameAsShipTo && (
                      <div className="space-y-2">
                        <Label htmlFor="billToId">Customer *</Label>
                        <Select
                          value={formData.billToId}
                          onValueChange={(value) => handleInputChange('billToId', value)}
                        >
                          <SelectTrigger className="border border-gray-300 rounded-xl">
                            <SelectValue placeholder="Select Customer">
                              {customers.find((c) => c.id?.toString() === formData.billToId)?.customerName}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContentWithSearch
                            className="min-w-[min(100vw-2rem,28rem)] max-w-[min(100vw-2rem,28rem)]"
                            searchPlaceholder="Search customer..."
                            debounceMs={300}
                          >
                            {customers.map((customer) => (
                              <SelectItem
                                key={customer.id}
                                value={customer.id?.toString() || ''}
                                textValue={customerSelectSearchText(customer)}
                                className="items-start py-2"
                              >
                                <CustomerSelectOptionLabel customer={customer} />
                              </SelectItem>
                            ))}
                          </SelectContentWithSearch>
                        </Select>
                      </div>
                    )}
                  </div>

                  {selectedBillTo && (
                    <div className="space-y-1 text-sm">
                      {selectedBillTo.address ? (
                        <div className="text-gray-600">{selectedBillTo.address}</div>
                      ) : null}
                      <div className="text-gray-600">Vendor Code: {selectedBillTo.customerCode}</div>
                      <div className="text-gray-600">GSTIN: {selectedBillTo.gstNo}</div>
                      <div className="text-gray-600">{selectedBillTo.stateName}, Code: {selectedBillTo.stateCode}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Specifics */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Calculator className="h-5 w-5 text-primary-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Invoice Details</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
                  {isEdit && (
                    <div className="space-y-2">
                      <Label>Invoice Number</Label>
                      <Input
                        value={formData.invoiceNo}
                        disabled
                        className="border border-gray-300 rounded-xl bg-gray-50"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="invoiceDate">Dated</Label>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
                      <Input
                        id="invoiceDate"
                        type="date"
                        value={formData.invoiceDate}
                        readOnly
                        className="border border-gray-300 rounded-xl min-w-0 flex-1 basis-[10rem] bg-gray-50"
                        disabled
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="deliverNote">Delivery Note</Label>
                    <Input
                      id="deliverNote"
                      value={formData.deliverNote}
                      onChange={(e) => handleInputChange('deliverNote', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="referenceNoAndDate">Reference No. & Date</Label>
                    <Input
                      id="referenceNoAndDate"
                      value={formData.referenceNoAndDate}
                      onChange={(e) => handleInputChange('referenceNoAndDate', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="buyerOrderNo">Buyer's Order No</Label>
                    <Input
                      id="buyerOrderNo"
                      value={formData.buyerOrderNo}
                      onChange={(e) => handleInputChange('buyerOrderNo', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="buyerOrderDate">Order Date</Label>
                    <Input
                      id="buyerOrderDate"
                      type="date"
                      value={formData.buyerOrderDate}
                      onChange={(e) => handleInputChange('buyerOrderDate', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="dispatchDocNo">Dispatch Doc No</Label>
                    <Input
                      id="dispatchDocNo"
                      value={formData.dispatchDocNo}
                      onChange={(e) => handleInputChange('dispatchDocNo', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="deliverNoteDate">Delivery Note Date</Label>
                    <Input
                      id="deliverNoteDate"
                      type="date"
                      value={formData.deliverNoteDate}
                      onChange={(e) => handleInputChange('deliverNoteDate', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="dispatchedThrough">Dispatch Through</Label>
                    <Input
                      id="dispatchedThrough"
                      value={formData.dispatchedThrough}
                      onChange={(e) => handleInputChange('dispatchedThrough', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ewayTransportMode">Mode of Transport</Label>
                    <Select
                      value={formData.ewayTransportMode || '__none__'}
                      onValueChange={(value) =>
                        handleInputChange('ewayTransportMode', value === '__none__' ? '' : value)
                      }
                    >
                      <SelectTrigger id="ewayTransportMode" className="border border-gray-300 rounded-xl">
                        <SelectValue placeholder="Select mode of transport" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {invoiceConfig.modesOfTransportation.map((mode) => (
                          <SelectItem key={mode} value={mode}>
                            {mode}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="destination">Destination</Label>
                    <Input
                      id="destination"
                      value={formData.destination}
                      onChange={(e) => handleInputChange('destination', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="billOfLanding">Bill of Lading</Label>
                    <Input
                      id="billOfLanding"
                      value={formData.billOfLanding}
                      onChange={(e) => handleInputChange('billOfLanding', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="motorVehicleNo">Motor Vehicle No</Label>
                    <Input
                      id="motorVehicleNo"
                      value={formData.motorVehicleNo}
                      onChange={(e) => handleInputChange('motorVehicleNo', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="termOfDelivery">Term of Delivery</Label>
                    <Input
                      id="termOfDelivery"
                      value={formData.termOfDelivery}
                      onChange={(e) => handleInputChange('termOfDelivery', e.target.value)}
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ewayDistanceKm">Distance (KM)</Label>
                    <Input
                      id="ewayDistanceKm"
                      value={formData.ewayDistanceKm}
                      onChange={(e) => handleDistanceChange(e.target.value)}
                      inputMode="decimal"
                      placeholder="e.g. 125 or 125.5"
                      className="border border-gray-300 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2 col-span-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="transporterMasterId">Transporter</Label>
                      {!isFieldDisabled() && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => setIsTransporterDialogOpen(true)}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Add
                        </Button>
                      )}
                    </div>
                    <Select
                      value={formData.transporterMasterId || '__none__'}
                      onValueChange={(value) =>
                        handleInputChange('transporterMasterId', value === '__none__' ? '' : value)
                      }
                      disabled={isFieldDisabled()}
                    >
                      <SelectTrigger
                        id="transporterMasterId"
                        className="border border-gray-300 rounded-xl min-w-0 w-full [&>span]:block [&>span]:truncate [&>span]:text-left"
                      >
                        <SelectValue placeholder="Select transporter" />
                      </SelectTrigger>
                      <SelectContentWithSearch searchPlaceholder="Search transporter..." debounceMs={200}>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {[...transporters]
                          .sort((a, b) =>
                            getTransporterDisplayName(a).localeCompare(getTransporterDisplayName(b), undefined, {
                              sensitivity: 'base',
                            })
                          )
                          .map((t) => (
                            <SelectItem key={t.id} value={String(t.id)}>
                              {getTransporterDisplayName(t)}
                              {t.transporterId ? ` (${t.transporterId})` : ''}
                            </SelectItem>
                          ))}
                      </SelectContentWithSearch>
                    </Select>
                  </div>
                  
                  {/* Sales order dropdown — not used for now
                  <div className="space-y-2">
                    <Label htmlFor="salesOrderId">Sales Order</Label>
                    <Select
                      value={formData.salesOrderId}
                      onValueChange={(value) => handleInputChange('salesOrderId', value)}
                    >
                      <SelectTrigger className="border border-gray-300 rounded-xl">
                        <SelectValue placeholder="Select Sales Order" />
                      </SelectTrigger>
                      <SelectContentWithSearch searchPlaceholder="Search sales order..." debounceMs={300}>
                        {salesOrders.map((so) => (
                          <SelectItem key={so.id} value={so.id?.toString() || ''}>
                            {so.soNumber} - {so.customerName || 'Unknown Customer'}
                          </SelectItem>
                        ))}
                      </SelectContentWithSearch>
                    </Select>
                  </div>
                  */}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Itemized Goods Table */}
        <Card className="border-2 border-primary-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-t-xl">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-primary-800">Itemized Goods</CardTitle>
              <div className="flex space-x-2">
                <Button
                  type="button"
                  onClick={addOtherCharge}
                  variant="outline"
                  className="border-primary-200 text-primary-700 hover:bg-primary-50"
                  size="sm"
                  disabled={isFieldDisabled()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Other Charges
                </Button>
                <Button
                  type="button"
                  onClick={addManualEntryDetail}
                  variant="outline"
                  className="border-primary-600 text-primary-600 hover:bg-primary-50"
                  size="sm"
                  disabled={isFieldDisabled()}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Manual Entry
                </Button>
                <Button
                  type="button"
                  onClick={addServiceEntryDetail}
                  variant="outline"
                  className="border-primary-600 text-primary-600 hover:bg-primary-50"
                  size="sm"
                  disabled={isFieldDisabled()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Services
                </Button>
                <Button
                  type="button"
                  onClick={addInvoiceDetail}
                  className="bg-primary-600 hover:bg-primary-700 text-white"
                  size="sm"
                  disabled={isFieldDisabled()}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Goods
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {formData.invoiceDetails.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">#</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Description of Goods</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">HSN Code</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Boxes</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Heat No/Container</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Quantity</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Rate</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Per</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Amount</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.invoiceDetails.map((detail, index) => (
                    <tr key={detail.id} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2">{index + 1}</td>
                      <td className="border border-gray-300 px-3 py-2">
                        {detail.isServiceEntry ? (
                          <Input
                            value={detail.manualDescription ?? ''}
                            onChange={(e) => updateInvoiceDetail(index, 'manualDescription', e.target.value)}
                            className="border rounded h-8"
                            placeholder={detail.isServiceEntry ? 'Service description' : 'Enter description'}
                            disabled={isFieldDisabled()}
                          />
                        ) : (
                          <Select
                            value={detail.endgoodId}
                            onValueChange={(value) => handleEndgoodSelection(value, index)}
                          >
                            <SelectTrigger className="border-0 p-0 h-auto">
                              <SelectValue placeholder="Select End Good (Packed)" />
                            </SelectTrigger>
                            <SelectContentWithSearch searchPlaceholder="Search end goods..." debounceMs={300}>
                              {packedEndgoods.map((endgood) => (
                                <SelectItem key={endgood.id} value={endgood.id?.toString() || ''}>
                                  {endgood.endgoodName}
                                </SelectItem>
                              ))}
                            </SelectContentWithSearch>
                          </Select>
                        )}
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        {detail.isServiceEntry ? (
                          <Select
                            value={detail.hsn}
                            onValueChange={(value) => updateInvoiceDetail(index, 'hsn', value)}
                          >
                            <SelectTrigger className="border rounded h-8 min-w-[100px]">
                              <SelectValue placeholder="Select HSN" />
                            </SelectTrigger>
                            <SelectContentWithSearch searchPlaceholder="Search HSN code or description..." debounceMs={300}>
                              {hsnCodes.map((h) => (
                                <SelectItem key={h.id} value={h.hsnCode}>
                                  {h.hsnCode} {h.description ? `- ${h.description}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContentWithSearch>
                          </Select>
                        ) : (
                          <span className="text-sm text-gray-700">{detail.hsn || '—'}</span>
                        )}
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        {(detail.isManualEntry || detail.isServiceEntry) ? (
                          detail.isServiceEntry ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            <Input
                              value={detail.boxes}
                              onChange={(e) => updateInvoiceDetail(index, 'boxes', e.target.value)}
                              className="border-0 p-0 h-auto"
                              placeholder="Boxes"
                              disabled={isFieldDisabled()}
                            />
                          )
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-auto py-1 px-2 text-left font-normal text-gray-700 hover:bg-gray-100"
                            onClick={() => openBoxSelectionDialog(index)}
                            disabled={!detail.endgoodId || isFieldDisabled()}
                          >
                            {detail.selectedPackingDetailIds.length > 0
                              ? `${detail.selectedPackingDetailIds.length} box${detail.selectedPackingDetailIds.length > 1 ? 'es' : ''} selected`
                              : 'Select Boxes'}
                            <ChevronDown className="h-4 w-4 text-gray-400 ml-1 flex-shrink-0" />
                          </Button>
                        )}
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        <Input
                          value={detail.userProvidedHeatNo}
                          onChange={(e) => updateInvoiceDetail(index, 'userProvidedHeatNo', e.target.value)}
                          className="border-0 p-0 h-auto"
                          placeholder="Heat No"
                        />
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm">
                        {detail.manualQuantityRate || detail.isManualEntry || detail.isServiceEntry ? (
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={
                              qtyDrafts[index] ??
                              (detail.qty && detail.qty !== 0 ? detail.qty.toString() : '')
                            }
                            onChange={(e) => setQtyDrafts((prev) => ({ ...prev, [index]: e.target.value }))}
                            onBlur={() => {
                              // No draft means user only focused/blurred — keep stored qty (avoid forcing 0 → empty display)
                              if (qtyDrafts[index] === undefined) return;
                              const raw = qtyDrafts[index];
                              const parsed = raw.trim() === '' ? NaN : parseFloat(raw);
                              updateInvoiceDetail(index, 'qty', !isNaN(parsed) ? parsed : 0);
                              setQtyDrafts((prev) => {
                                const next = { ...prev };
                                delete next[index];
                                return next;
                              });
                            }}
                            className="h-8 w-24"
                            placeholder={detail.isServiceEntry ? 'Optional' : undefined}
                            disabled={isFieldDisabled()}
                          />
                        ) : (
                          <span className="text-gray-700">{detail.qty}</span>
                        )}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-sm">
                        {detail.manualQuantityRate || detail.isManualEntry || detail.isServiceEntry || detail.allowRateEdit ? (
                          <Input
                            type="text"
                            inputMode="decimal"
                            step={0.01}
                            value={
                              rateDrafts[index] ??
                              (detail.rate != null && detail.rate !== 0 ? detail.rate.toString() : '')
                            }
                            onChange={(e) => setRateDrafts((prev) => ({ ...prev, [index]: e.target.value }))}
                            onBlur={() => {
                              if (rateDrafts[index] === undefined) return;
                              const raw = rateDrafts[index];
                              const parsed = raw.trim() === '' ? NaN : parseFloat(raw);
                              updateInvoiceDetail(index, 'rate', !isNaN(parsed) ? parsed : 0);
                              setRateDrafts((prev) => {
                                const next = { ...prev };
                                delete next[index];
                                return next;
                              });
                            }}
                            className="h-8 w-24"
                            placeholder={detail.isServiceEntry ? 'Optional' : 'Rate'}
                            disabled={isFieldDisabled()}
                          />
                        ) : (
                          <span className="text-gray-700">{detail.rate != null && detail.rate > 0 ? detail.rate.toFixed(2) : '—'}</span>
                        )}
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        <Input
                          value={detail.uom}
                          onChange={(e) => updateInvoiceDetail(index, 'uom', e.target.value)}
                          className="border-0 p-0 h-auto"
                          placeholder="UOM"
                        />
                      </td>
                      <td className="border border-gray-300 px-3 py-2 font-semibold">
                        {detail.isServiceEntry ? (
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={detail.amount ?? ''}
                            onChange={(e) => updateInvoiceDetail(index, 'amount', parseFloat(e.target.value) || 0)}
                            className="h-8 w-24 font-semibold"
                            placeholder="Amount"
                            disabled={isFieldDisabled()}
                          />
                        ) : (
                          <>₹{(detail.amount ?? 0).toFixed(2)}</>
                        )}
                      </td>
                      <td className="border border-gray-300 px-3 py-2">
                        <div className="flex items-center gap-2">
                          {!detail.isManualEntry && !detail.isServiceEntry && (
                            <Button
                              type="button"
                              variant={detail.allowRateEdit ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => toggleRateEdit(index)}
                              className={detail.allowRateEdit ? 'bg-primary-600 hover:bg-primary-700 text-white' : 'border-primary-200 text-primary-700 hover:bg-primary-50'}
                              disabled={isFieldDisabled()}
                              title={detail.allowRateEdit ? 'Disable rate editing' : 'Enable rate editing'}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeInvoiceDetail(index)}
                            className="text-red-600 hover:text-red-700 border-red-200"
                            disabled={isFieldDisabled()}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            ) : (
              <p className="text-gray-500 py-4">No items added yet. Click &quot;Add Goods&quot; to add a line.</p>
            )}
          </CardContent>
        </Card>

        {/* Other Charges */}
        {formData.otherCharges.length > 0 && (
          <Card className="border-2 border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-t-xl">
              <CardTitle className="text-xl font-bold text-primary-800">Other Charges</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {formData.otherCharges.map((charge, index) => (
                  <div key={charge.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-700">Charge {index + 1}</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeOtherCharge(index)}
                        className="text-red-600 hover:text-red-700 border-red-200"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label className="text-red-600 font-semibold">
                          Charge Type <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={charge.chargeMasterId}
                          onValueChange={async (value) => {
                            updateOtherCharge(index, 'chargeMasterId', value);
                            if (!value) return;
                            const list = effectiveChargeMasters;
                            const fromList = list.find((cm) => String(cm.id) === value) as ChargeMaster | undefined;
                            if (fromList && (fromList.description != null || fromList.defaultRate != null)) {
                              applyChargeMasterToOtherCharge(index, fromList);
                              return;
                            }
                            try {
                              const res = await invoiceApiService.getChargeMasterById(value);
                              const cm = res?.data;
                              if (cm && typeof cm === 'object') {
                                applyChargeMasterToOtherCharge(index, cm as ChargeMaster);
                              }
                            } catch {
                              toast.error('Failed to load charge details');
                            }
                          }}
                        >
                          <SelectTrigger className={`border rounded-xl ${
                            !charge.chargeMasterId || charge.chargeMasterId === '' 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300'
                          }`}>
                            <SelectValue placeholder="Select Charge Type (Required)" />
                          </SelectTrigger>
                          <SelectContent>
                            {effectiveChargeMasters.map((cm) => (
                              <SelectItem key={cm.id} value={cm.id?.toString() || ''}>
                                {cm.chargeName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(!charge.chargeMasterId || charge.chargeMasterId === '') && (
                          <p className="text-red-500 text-sm">Please select a charge type</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <p className="text-sm text-gray-700 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50 min-h-[2.5rem] flex items-center">
                          {charge.description || '—'}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Rate (%)</Label>
                        <p className="text-sm text-gray-700 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50 min-h-[2.5rem] flex items-center">
                          {charge.rate === 0 ? '—' : charge.rate}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Amount</Label>
                        {((charge.applicableOn ?? '').trim().toUpperCase() === 'MANUAL') ? (
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={charge.amount}
                            onChange={(e) => {
                              const parsed = parseFloat(e.target.value) || 0;
                              const masters = Array.isArray(chargeMasters) ? chargeMasters : [];
                              const v = isTcsOtherCharge(charge, masters)
                                ? roundTcsToWholeRupee(parsed)
                                : parsed;
                              updateOtherCharge(index, 'amount', v);
                            }}
                            className="border border-gray-300 rounded-xl"
                          />
                        ) : (
                          <>
                            <p className="text-sm text-gray-700 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50 min-h-[2.5rem] flex items-center">
                              {getEffectiveOtherChargeAmount(charge)}
                            </p>
                            {isChargeComputedFromTotal(charge) && (
                              <p className="text-xs text-gray-500">{charge.rate}% of (Subtotal + Tax) — computed automatically</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary */}
        <Card className="border-2 border-primary-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-t-xl">
            <CardTitle className="text-xl font-bold text-primary-800">Invoice Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex justify-between text-lg">
                  <span className="font-medium">Subtotal:</span>
                  <span>₹{calculateSubtotal().toFixed(2)}</span>
                </div>
                
                {getTaxType() === 'cgst_sgst' ? (
                  <>
                    <div className="flex justify-between text-lg">
                      <span className="font-medium">CGST:</span>
                      <span>₹{calculateTotalCGST().toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between text-lg">
                      <span className="font-medium">SGST:</span>
                      <span>₹{calculateTotalSGST().toFixed(2)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-lg">
                    <span className="font-medium">IGST:</span>
                    <span>₹{calculateTotalIGST().toFixed(2)}</span>
                  </div>
                )}
                
                {formData.otherCharges.length > 0 && (
                  <div className="space-y-1">
                    {formData.otherCharges
                      .filter((charge) => charge.chargeMasterId && charge.chargeMasterId !== '')
                      .map((charge) => {
                        const effectiveAmount = getEffectiveOtherChargeAmount(charge);
                        if (!effectiveAmount) return null;
                        const label = charge.description || 'Other Charge';
                        return (
                          <div
                            key={`${charge.chargeMasterId}-${charge.sequenceOrder}-${charge.id}`}
                            className="flex justify-between text-lg"
                          >
                            <span className="font-medium">{label}:</span>
                            <span>₹{effectiveAmount.toFixed(2)}</span>
                          </div>
                        );
                      })}
                  </div>
                )}
                
                <Separator />
                
                <div className="flex justify-between text-2xl font-bold text-primary-800">
                  <span>Grand Total:</span>
                  <span>₹{calculateGrandTotal().toFixed(2)}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Amount Chargeable (in words)</Label>
                  <p className="text-sm text-gray-700 border border-gray-200 rounded-xl p-3 bg-gray-50 min-h-[4.5rem]">
                    INR {amountInWords(calculateGrandTotal())}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Tax Amount (in words)</Label>
                  <p className="text-sm text-gray-700 border border-gray-200 rounded-xl p-3 bg-gray-50 min-h-[4.5rem]">
                    INR {amountInWords(totalTaxAmount())}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additionalRemarks">Additional Remarks</Label>
                  <Textarea
                    id="additionalRemarks"
                    value={formData.additionalRemarks}
                    onChange={(e) => handleInputChange('additionalRemarks', e.target.value)}
                    className="border border-gray-300 rounded-xl min-h-[90px]"
                    placeholder="Enter additional remarks"
                  />
                </div>
                
                <div className="text-sm text-gray-600">
                  Company's PAN: {selectedSeller?.panNo || 'N/A'}
                </div>
              </div>
            </div>

            {/* HSN-wise Tax Summary Table */}
            {formData.invoiceDetails.some((d) => d.endgoodId && d.amount > 0) && (
              <div className="mt-6 border rounded-xl overflow-hidden">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="border border-gray-300 p-2 text-center">HSN</th>
                      <th className="border border-gray-300 p-2 text-center">Taxable Value</th>
                      {getTaxType() === 'cgst_sgst' ? (
                        <>
                          <th className="border border-gray-300 p-2 text-center">CGST Rate</th>
                          <th className="border border-gray-300 p-2 text-center">CGST Amount</th>
                          <th className="border border-gray-300 p-2 text-center">SGST Rate</th>
                          <th className="border border-gray-300 p-2 text-center">SGST Amount</th>
                        </>
                      ) : (
                        <>
                          <th className="border border-gray-300 p-2 text-center">IGST Rate</th>
                          <th className="border border-gray-300 p-2 text-center">IGST Amount</th>
                        </>
                      )}
                      <th className="border border-gray-300 p-2 text-center">Total Tax Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hsnWiseTaxDetails.map((row, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="border border-gray-300 p-2">{row.hsn}</td>
                        <td className="border border-gray-300 p-2">{row.taxableValue.toFixed(2)}</td>
                        {getTaxType() === 'cgst_sgst' ? (
                          <>
                            <td className="border border-gray-300 p-2 text-center">{row.cgstRate}%</td>
                            <td className="border border-gray-300 p-2 text-right">{row.cgstAmount.toFixed(2)}</td>
                            <td className="border border-gray-300 p-2 text-center">{row.sgstRate}%</td>
                            <td className="border border-gray-300 p-2 text-right">{row.sgstAmount.toFixed(2)}</td>
                          </>
                        ) : (
                          <>
                            <td className="border border-gray-300 p-2 text-center">{row.igstRate}%</td>
                            <td className="border border-gray-300 p-2 text-right">{row.igstAmount.toFixed(2)}</td>
                          </>
                        )}
                        <td className="border border-gray-300 p-2 text-right">{row.totalTaxAmount.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="border border-gray-300 p-2">TOTAL</td>
                      <td className="border border-gray-300 p-2">{calculateSubtotal().toFixed(2)}</td>
                      {getTaxType() === 'cgst_sgst' ? (
                        <>
                          <td className="border border-gray-300 p-2"></td>
                          <td className="border border-gray-300 p-2 text-right">{calculateTotalCGST().toFixed(2)}</td>
                          <td className="border border-gray-300 p-2"></td>
                          <td className="border border-gray-300 p-2 text-right">{calculateTotalSGST().toFixed(2)}</td>
                        </>
                      ) : (
                        <>
                          <td className="border border-gray-300 p-2"></td>
                          <td className="border border-gray-300 p-2 text-right">{calculateTotalIGST().toFixed(2)}</td>
                        </>
                      )}
                      <td className="border border-gray-300 p-2 text-right">{totalTaxAmount().toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Declaration */}
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-700">
                We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/invoices')}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-gray-500 hover:bg-gray-600 text-white"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                  {isEdit ? 'UPDATE INVOICE' : 'SAVE AND PREVIEW'}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Box selection popup: enter number to auto-select, or select multiple boxes */}
      <Dialog open={boxSelectionDetailIndex !== null} onOpenChange={(open) => !open && setBoxSelectionDetailIndex(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select boxes</DialogTitle>
          </DialogHeader>
          {boxSelectionDetailIndex !== null && (() => {
            const detail = formData.invoiceDetails[boxSelectionDetailIndex];
            const available = detail?.endgoodId ? getAvailableBoxTypesForEndgood(detail.endgoodId, detail.id) : [];
            if (!detail?.endgoodId) {
              return (
                <p className="text-sm text-gray-500">Select an end good first.</p>
              );
            }
            if (available.length === 0) {
              return (
                <p className="text-sm text-gray-500">No box available.</p>
              );
            }
            const savedIds = detail?.selectedPackingDetailIds ?? [];
            const availableIds = new Set(available.map((pd) => pd.id));
            const displayList: PackingDetail[] = [...available];
            savedIds.forEach((savedId) => {
              if (savedId && !availableIds.has(savedId)) {
                displayList.push({
                  id: savedId,
                  headerBatchId: 0,
                  boxNumber: `Box #${savedId}`,
                  status: 'FULL' as any,
                  quantity: 0,
                  notes: 'Previously saved'
                });
                availableIds.add(savedId);
              }
            });
            const totalAvailable = displayList.length;
            return (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Auto-select by number</Label>
                  <p className="text-sm font-medium text-gray-700">
                    Total available: {totalAvailable} box{totalAvailable !== 1 ? 'es' : ''}
                  </p>
                  <p className="text-xs text-gray-500">
                    Enter a number (max {totalAvailable}) to select that many boxes from the list (first N).
                  </p>
                  <Input
                    type="number"
                    min={0}
                    max={totalAvailable}
                    value={boxAutoSelectCount > 0 ? boxAutoSelectCount : boxSelectedIds.length}
                    onChange={(e) => {
                      const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                      setBoxAutoSelectCount(isNaN(v) ? 0 : Math.max(0, Math.min(totalAvailable, v)));
                    }}
                    placeholder="e.g. 10"
                    className="w-32"
                  />
                </div>
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <Label className="text-sm font-medium">Or select boxes</Label>
                  <p className="text-xs text-gray-500">Check the boxes you want to include. All currently selected (including saved) are shown.</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto py-1">
                    {displayList.map((pd) => {
                      const id = pd.id ?? 0;
                      return (
                        <label
                          key={pd.id}
                          className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-gray-50"
                        >
                          <Checkbox
                            checked={boxSelectedIds.includes(id)}
                            onCheckedChange={() => toggleBoxSelectedInDialog(id)}
                            disabled={boxAutoSelectCount > 0}
                          />
                          <span className="text-sm">
                            {pd.boxNumber} ({pd.quantity ?? 0} unit{pd.quantity !== 1 ? 's' : ''})
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setBoxSelectionDetailIndex(null)}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={applyBoxSelection}>
                    Apply
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Create transporter popup */}
      <Dialog open={isTransporterDialogOpen} onOpenChange={setIsTransporterDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create transporter</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="newTransporterName">Name</Label>
              <Input
                id="newTransporterName"
                value={newTransporter.transporterName}
                onChange={(e) =>
                  setNewTransporter((prev) => ({ ...prev, transporterName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="newTransporterCode">GSTIN</Label>
              <Input
                id="newTransporterCode"
                value={newTransporter.transporterId}
                onChange={(e) =>
                  setNewTransporter((prev) => ({ ...prev, transporterId: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsTransporterDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleCreateTransporter}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
